import {
  Archive,
  Browsers,
  CaretDownIcon,
  CaretRightIcon,
  ClockCounterClockwise,
  CubeIcon,
  DatabaseIcon,
  FilmStrip,
  FlowArrow,
  Key,
  PlugsConnected,
  Pulse,
  Queue as QueueIcon
} from "@phosphor-icons/react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useRef } from "react";
import { useResourceTabFocus } from "./editor/react.tsx";
import type { ResourceTabKind } from "./editor/layout/resource-focus-model.ts";

export type ResourceExplorerKind = ResourceTabKind;

export function ResourceExplorerPanel({
  active,
  kind,
  children
}: {
  active: ResourceExplorerKind;
  kind: ResourceExplorerKind;
  children: React.ReactNode;
}) {
  return (
    <div
      id={`resource-panel-${kind}`}
      role="tabpanel"
      aria-labelledby={`resource-tab-${kind}`}
      hidden={active !== kind}
      className="h-full min-h-0"
    >
      {children}
    </div>
  );
}

export const RESOURCE_TABS: Array<{
  id: ResourceExplorerKind;
  label: string;
  icon: React.ReactNode;
}> = [
  { id: "databases", label: "Databases", icon: <DatabaseIcon size={13} /> },
  { id: "workflows", label: "Workflows", icon: <FlowArrow size={13} /> },
  { id: "containers", label: "Containers", icon: <CubeIcon size={13} /> },
  { id: "r2", label: "R2", icon: <Archive size={13} /> },
  { id: "kv", label: "KV", icon: <Key size={13} /> },
  { id: "queues", label: "Queues", icon: <QueueIcon size={13} /> },
  {
    id: "workers",
    label: "Worker bindings",
    icon: <PlugsConnected size={13} />
  },
  {
    id: "browser",
    label: "Browser observability",
    icon: <Browsers size={13} />
  },
  {
    id: "flow-recordings",
    label: "Flow recordings",
    icon: <FilmStrip size={13} />
  },
  {
    id: "history",
    label: "History",
    icon: <ClockCounterClockwise size={13} />
  },
  {
    id: "traces",
    label: "Traces",
    icon: <Pulse size={13} />
  }
];

export function ResourceExplorerTabs({
  active,
  collapsed,
  onActiveChange,
  onCollapsedChange
}: {
  active: ResourceExplorerKind;
  collapsed: boolean;
  onActiveChange: (kind: ResourceExplorerKind) => void;
  onCollapsedChange: (collapsed: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null), focusTab = useResourceTabFocus(ref, active);
  const activate = (kind: ResourceExplorerKind, focus = false) => {
    onActiveChange(kind);
    if (collapsed) onCollapsedChange(false);
    if (focus) {
      focusTab(kind);
    }
  };

  const onTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    let nextIndex: number | undefined;
    if (event.key === "ArrowRight") {
      nextIndex = (index + 1) % RESOURCE_TABS.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex = (index - 1 + RESOURCE_TABS.length) % RESOURCE_TABS.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = RESOURCE_TABS.length - 1;
    }
    if (nextIndex === undefined) return;
    event.preventDefault();
    activate(RESOURCE_TABS[nextIndex].id, true);
  };

  return (
    <div ref={ref} className="flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden">
      <button
        type="button"
        aria-label={
          collapsed ? "Expand resource explorer" : "Collapse resource explorer"
        }
        aria-expanded={!collapsed}
        aria-controls={`resource-panel-${active}`}
        title={
          collapsed ? "Expand resource explorer" : "Collapse resource explorer"
        }
        onClick={() => onCollapsedChange(!collapsed)}
        className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded bg-transparent text-kumo-subtle hover:bg-kumo-fill hover:text-kumo-default"
      >
        {collapsed ? (
          <CaretRightIcon size={10} weight="bold" />
        ) : (
          <CaretDownIcon size={10} weight="bold" />
        )}
      </button>
      <Tooltip.Provider
        delayDuration={0}
        skipDelayDuration={0}
        disableHoverableContent
      >
        <div
          role="tablist"
          aria-label="Resource explorers"
          aria-orientation="horizontal"
          className="flex min-w-0 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {RESOURCE_TABS.map((tab, index) => (
            <Tooltip.Root key={tab.id}>
              <Tooltip.Trigger asChild>
                <button
                  id={`resource-tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={active === tab.id}
                  aria-controls={`resource-panel-${tab.id}`}
                  aria-label={tab.label}
                  tabIndex={active === tab.id ? 0 : -1}
                  onClick={() => activate(tab.id, true)}
                  onKeyDown={(event) => onTabKeyDown(event, index)}
                  className={`flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded border bg-transparent transition-colors ${
                    active === tab.id
                      ? "border-kumo-accent/40 bg-kumo-fill text-kumo-accent"
                      : "border-transparent text-kumo-subtle hover:bg-kumo-fill hover:text-kumo-default"
                  }`}
                >
                  {tab.icon}
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="top"
                  sideOffset={4}
                  collisionPadding={4}
                  className="z-[9999] rounded border border-kumo-line bg-kumo-base px-2 py-1 text-[10px] font-medium text-kumo-default shadow-lg"
                >
                  {tab.label}
                  <Tooltip.Arrow className="fill-kumo-base" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          ))}
        </div>
      </Tooltip.Provider>
    </div>
  );
}
